import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, Users, Trash2, X, Link, Copy, CreditCard } from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import { bookingsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { useState } from 'react';

const BookingsTab = ({
  bookings,
  navigate,
  onRefresh
}) => {
  const [deleting, setDeleting] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleDelete = async (booking) => {
    const studentName = booking.student_name;
    const bookingIds = booking.booking_ids || [booking.id];
    
    if (!window.confirm(`Are you sure you want to delete all bookings for "${studentName}" (${bookingIds.length} class${bookingIds.length > 1 ? 'es' : ''})? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(booking.id);
    try {
      // Delete all individual bookings in the group
      const deletePromises = bookingIds.map(id => bookingsAPI.adminDelete(id));
      await Promise.all(deletePromises);
      toast.success(`${bookingIds.length} booking(s) deleted successfully`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete booking');
    } finally {
      setDeleting(null);
    }
  };

  const copyCardLink = (bookingId) => {
    const link = `${window.location.origin}/add-card-for-booking?bookingId=${bookingId}`;
    navigator.clipboard.writeText(link);
    setCopiedId(bookingId);
    toast.success('Card link copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div data-testid="bookings-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-semibold text-white">Recent Bookings</h2>
        <Button 
          variant="outline"
          onClick={() => navigate('/admin/students')}
          className="border-[#333] text-white hover:bg-[#222]"
        >
          <Users className="w-4 h-4 mr-2" /> View All Students
        </Button>
      </div>

      <Card className="bg-[#111] border-[#333]">
        <CardContent className="p-0">
          {bookings.length === 0 ? (
            <div className="py-12 text-center text-white/50">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p>No bookings yet</p>
            </div>
          ) : (
            <div className="divide-y divide-[#333]">
              {bookings.slice(0, 20).map(booking => (
                <div 
                  key={booking.id} 
                  className="flex items-center justify-between p-4 hover:bg-[#1a1a1a] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF007F]/20 rounded-full flex items-center justify-center">
                      <span className="text-[#FF007F] font-bold">
                        {booking.student_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-white">
                        {booking.student_name}
                        {booking.class_count > 1 && (
                          <Badge className="ml-2 bg-[#7000FF]/20 text-[#7000FF] border-[#7000FF]/30 text-xs">
                            {booking.class_count} classes
                          </Badge>
                        )}
                      </p>
                      <p className="text-sm text-white/50">
                        {booking.class_names?.join(', ') || booking.class_name}
                      </p>
                      <p className="text-xs text-white/30">
                        {booking.created_at 
                          ? new Date(booking.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })
                          : 'N/A'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <Badge 
                        className={
                          booking.payment_status === 'paid' 
                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' 
                            : booking.payment_status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                            : 'bg-[#333] text-white/50'
                        }
                      >
                        {booking.payment_status || 'unknown'}
                      </Badge>
                      <p className="text-sm font-mono mt-1 text-white">
                        {formatPrice(booking.amount)}
                      </p>
                    </div>
                    {/* Card Link button for pending bookings */}
                    {booking.payment_status === 'pending' && (
                      <button
                        onClick={() => copyCardLink(booking.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          copiedId === booking.id 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'hover:bg-blue-500/20 text-blue-400 hover:text-blue-300'
                        }`}
                        title="Copy card payment link"
                      >
                        {copiedId === booking.id ? (
                          <Copy className="w-4 h-4" />
                        ) : (
                          <CreditCard className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(booking)}
                      disabled={deleting === booking.id}
                      className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                      title="Delete booking"
                    >
                      {deleting === booking.id ? (
                        <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingsTab;
